import { Button, Grid, IconButton, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useEffect, useState } from "react";
import useValidations from "../../../hooks/useValidations";
import { lang } from "../../../i18nLang";
import CustomColumnControl from "../CustomColumnControl";
import { ColumnControl, Title } from "./styles";

const customNameText = "customField-";

const useStyles = makeStyles({
  fieldRow: {
    "& .actionButton": {
      display: "none",
    },
    "&:hover .actionButton": {
      display: "unset",
    },
  },
});

interface TabCustomColumnsProps {
  /** Objeto com as colunas referentes as propriedades personalizadas. */
  values: {};
  /** Objeto com as validações para cada propriedade personalizada */
  validations?: {};
  /** Objeto com os dados para validar o campo */
  validate?: {};
  /** Função chamada ao alterar o valor de uma ColumnField. */
  onChange: (name: string, key: string, value: string) => void;
  /** Função chamada ao alterar o objeto de erros dos campos. */
  onChangeErrors?: (hasErrors: boolean) => void;
  /** Dispara evento ao clicar em deletar campo personalizado. */
  onDelete: (name: string) => void;
}

/** Formulário para definir as propriedades customizadas para importação de contrato.
 * @category Components */
const TabCustomColumns = (props: TabCustomColumnsProps) => {
  const classes = useStyles();
  const [lastId, setLastId] = useState(0);
  const [customFieldsNames, setCustomFieldsNames] = useState(
    new Array<string>()
  );
  const { validated, validateFields, addFieldValidation } = useValidations(
    props.validations || {}
  );

  const handleClickNewCustomField = (fieldsNames?: string[]) => {
    const newCustomFieldsNodes: any = fieldsNames
      ? [...fieldsNames]
      : [...customFieldsNames];
    const name = `${customNameText}${lastId}`;

    newCustomFieldsNodes.push(name);
    addFieldValidation(name, props.validate);

    setCustomFieldsNames(newCustomFieldsNodes);
    setLastId(lastId + 1);
  };

  useEffect(() => {
    const getData = (names) => {
      const data = {};
      names.forEach((name) => {
        data[name] = props.values[name].value;
      });
      return data;
    };

    const validatedDatas = (names) => {
      const hasErrors = !validateFields(getData(names), true);
      if (props.onChangeErrors) {
        props.onChangeErrors(hasErrors);
      }
    };

    const newNames = Object.keys(props.values);
    if (newNames && newNames.length) {
      setCustomFieldsNames(newNames);
      setLastIdFromName(newNames[newNames.length - 1]);
      validatedDatas(newNames);
      return;
    }

    handleClickNewCustomField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const hasErrors = !validateFields({ [name]: value });
    if (props.onChangeErrors) {
      props.onChangeErrors(hasErrors);
    }
  };

  const handleClickDelete = (name: string) => {
    props.onDelete(name);

    const newCustomFieldsNames = customFieldsNames.filter(
      (fieldName) => fieldName !== name
    );
    setCustomFieldsNames(newCustomFieldsNames);
    if (newCustomFieldsNames.length === 0) {
      handleClickNewCustomField(newCustomFieldsNames);
    }
  };

  function setLastIdFromName(lastName: string) {
    const id = Number(lastName.replace(customNameText, ""));
    setLastId(id + 1);
  }

  return (
    <>
      <Title>{lang(`importation.modeFile.subtitle4`)}</Title>
      {customFieldsNames.map((name: string) => {
        const data = props.values[name];
        const fieldValidated = validated[name];

        return (
          <ColumnControl className={classes.fieldRow} container key={name}>
            <CustomColumnControl
              name={name}
              defaultKey={data?.field}
              defaultValue={data?.value}
              onChange={props.onChange}
              inputProps={{
                onBlur: handleBlur,
                error: !fieldValidated ? false : !fieldValidated.valid,
                helperText: fieldValidated?.text,
              }}
            />
            <Grid container item xs={1}>
              <IconButton
                className="actionButton"
                onClick={() => handleClickDelete(name)}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </ColumnControl>
        );
      })}
      <Button
        color="primary"
        variant="outlined"
        onClick={() => handleClickNewCustomField()}
        style={{ marginTop: "1rem" }}
      >
        {lang(`importation.modeFile.newProperty`)}
      </Button>
    </>
  );
};

export default TabCustomColumns;
